import React from 'react';
import { Destination, Product, Quote } from '../types/catalog';
import { SortKey, loadTotal, priceUnit } from '../utils/quotes';
import { formatCurrency, formatMiles } from '../utils/geo';
import { QuoteRow } from './QuoteRow';
interface QuoteResultsProps {
  product: Product;
  destination: Destination;
  quotes: Quote[];
  outsideRadius: number;
  radiusMiles: number;
  showAll: boolean;
  /** Load size priced out on every row, in the product's bulk unit. */
  loadUnits: number;
  sortBy: SortKey;
  onSortChange: (key: SortKey) => void;
  onRemoveManual: (entryId: string) => void;
}
export function QuoteResults({
  product,
  destination,
  quotes,
  outsideRadius,
  radiusMiles,
  showAll,
  loadUnits,
  sortBy,
  onSortChange,
  onRemoveManual
}: QuoteResultsProps) {
  if (quotes.length === 0) {
    return <div className="rounded-xl border border-line bg-surface px-6 py-14 text-center" data-id="element-116" data-mp="9b8x2c0nndd8">
        <p className="text-base font-medium text-ink" data-id="element-117" data-mp="4dz8eyl3b5qg">
          {outsideRadius > 0 ? `No plant within ${radiusMiles} mi` : 'No published price'}
        </p>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-muted" data-id="element-118" data-mp="3vt1r1ukbtg7l">
          {outsideRadius > 0 ? `${product.name} is priced at ${outsideRadius} plant${outsideRadius === 1 ? '' : 's'} outside the haul radius of ${destination.label}. Use “Show all” to see them.` : `${product.name} isn't listed for any plant on file — add a phoned-in price below.`}
        </p>
      </div>;
  }

  // Sellers who quote the same material in a different unit (a delivered
  // cubic yard vs a ton over the scale) are shown, but ranked separately.
  const inPrimaryUnit = quotes.filter(q => priceUnit(q.price, product) === product.unit);
  const inOtherUnit = quotes.filter(q => priceUnit(q.price, product) !== product.unit);
  const ranked = inPrimaryUnit.length > 0 ? inPrimaryUnit : quotes;
  const otherUnitLabel = inOtherUnit[0] && priceUnit(inOtherUnit[0].price, product);
  const bestPrice = Math.min(...ranked.map(q => q.price.price));
  const highPrice = Math.max(...ranked.map(q => q.price.price));
  const nearest = quotes.reduce((closest, quote) => quote.distanceMiles < closest.distanceMiles ? quote : closest);
  const loadSizes = ranked.map(quote => loadTotal(quote, product, loadUnits)).sort((a, b) => a.total - b.total);
  const cheapestLoad = loadSizes[0];
  const dearestLoad = loadSizes[loadSizes.length - 1];
  return <section aria-label={`Quarry prices for ${product.name}`} data-id="element-119" data-mp="2jfuuf2kzskdc">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-4" data-id="element-120" data-mp="2ekpd6h2jbtvl">
        <div data-id="element-121" data-mp="2t8ai8giz0365">
          <h2 className="text-xl font-semibold text-ink" data-id="element-122" data-mp="2t8sirpco13jv">{product.name}</h2>
          <p className="tnum mt-1 text-sm text-ink-muted" data-id="element-123" data-mp="2oh0q999y8nr3">
            {product.category}
            {product.cosSpec && ` · ${product.cosSpec}`}
            {product.productNumber && ` · #${product.productNumber}`} · {quotes.length} seller
            {quotes.length === 1 ? '' : 's'}{' '}
            {showAll ? 'at any distance from' : `within ${radiusMiles} mi of`}{' '}
            {destination.label} · {formatCurrency(bestPrice)}–{formatCurrency(highPrice)} per{' '}
            {ranked === quotes ? product.unit : priceUnit(ranked[0].price, product)}
            {inPrimaryUnit.length > 0 && inOtherUnit.length > 0 && ` · ${inOtherUnit.length} more priced per ${otherUnitLabel}`}
          </p>
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface p-1" data-id="element-124" data-mp="u1vefeev3rj1">
          <span className="sr-only" data-id="element-125" data-mp="yzdlt0d8dyox">Sort results</span>
          {[{
          key: 'price' as SortKey,
          label: 'Lowest price'
        }, {
          key: 'distance' as SortKey,
          label: 'Closest'
        }].map(option => <button key={option.key} type="button" onClick={() => onSortChange(option.key)} aria-pressed={sortBy === option.key} className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${sortBy === option.key ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`} data-id="element-126" data-mp="35qd1ztbgvcr5">
              {option.label}
            </button>)}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-line" data-id="element-127" data-mp="3c8r2md6tquid">
        <div className="hidden grid-cols-12 gap-4 border-b border-line bg-canvas px-5 py-2.5 text-xs font-medium uppercase tracking-wide text-ink-soft md:grid" data-id="element-128" data-mp="csuh0hph7e4t">
          <span className="col-span-5" data-id="element-129" data-mp="7uj3nwzz2lhu">Plant</span>
          <span className="col-span-2" data-id="element-130" data-mp="3rmrao10zlmc">Distance</span>
          <span className="col-span-2" data-id="element-131" data-mp="3qbfq21c99ko">Price / {product.unit}</span>
          <span className="tnum col-span-3 text-right" data-id="element-132" data-mp="3uxe6isd9ldud">
            {cheapestLoad.quantity} {cheapestLoad.unit} load
          </span>
        </div>
        <ul data-id="element-133" data-mp="2og0s88ekkja7">
          {quotes.map((quote, index) => {
          const otherUnit = priceUnit(quote.price, product) !== product.unit;
          const startsOtherUnit = otherUnit && inPrimaryUnit.length > 0 && index === inPrimaryUnit.length;
          return <React.Fragment key={quote.price.entryId ?? `${quote.quarry.id}-${index}`} data-id="element-134" data-mp="140kvgcvneo4k" data-mpi={index}>
                {startsOtherUnit && <li className="border-b border-line bg-canvas px-5 py-2 text-xs text-ink-muted" data-id="element-135" data-mp="1csece0f3a0uo">
                    Also sold by the {otherUnitLabel} — a different basis, so these aren't ranked
                    against the per-{product.unit} prices above.
                  </li>}
                <QuoteRow quote={quote} product={product} loadUnits={loadUnits} index={index} isBest={(ranked === quotes || !otherUnit) && quote.price.price === bestPrice} premiumVsBest={ranked === quotes || !otherUnit ? quote.price.price - bestPrice : 0} beyondRadius={quote.distanceMiles > radiusMiles} onRemoveManual={onRemoveManual} data-id="element-136" data-mp="1rq6qqp8wgj7a" />
              </React.Fragment>;
        })}
        </ul>
      </div>

      <p className="tnum px-1 pt-3 text-sm text-ink-muted" data-id="element-137" data-mp="1dqegtlbgynnl">
        Closest scale: {nearest.quarry.name}, {formatMiles(nearest.distanceMiles)} mi · a{' '}
        {cheapestLoad.quantity} {cheapestLoad.unit} load runs {formatCurrency(cheapestLoad.total, 0)}
        {dearestLoad.total > cheapestLoad.total && `–${formatCurrency(dearestLoad.total, 0)}, a spread of ${formatCurrency(dearestLoad.total - cheapestLoad.total, 0)}`}{' '}
        · load totals include each seller's surcharges, load minimums and delivery fees, before
        sales tax and trucking.
      </p>
    </section>;
}